import { PayableTableObject } from '../invoices/models/payable-table-object';
import { ConfigService } from '@aiwyn/config';
import { Store } from '@ngxs/store';
import { UpdateArTranAllocations } from '@aiwyn/common/payments';
import { Subject } from 'rxjs';
import { applicationDebounceTime, roundMonetaryValue } from '@aiwyn/shared';
import { map, switchMap, takeUntil } from 'rxjs/operators';

export class AbstractPartialPayment {

  // Active pay partial for invoice.
  payPartialActive = false;

  // Amount to pay (partial payment).
  amountToPay: number = null;

  // Ngx currency input config.
  min = 0;

  // True if pay partial input is valid.
  valid = true;

  protected readonly _unsubscribe$ = new Subject<void>();

  private readonly _arTranAllocationUpdate$ = new Subject<[PayableTableObject, number]>();
  onArTranAllocationUpdate$ = this._arTranAllocationUpdate$.pipe(
    applicationDebounceTime(),
    map(([payable, allocatedAmount]) => ({ arTranId: payable.invoice.arTran.id, allocatedAmount, active: payable.invoice.arTran.active })),
    switchMap(arTranAllocation => this.store.dispatch(new UpdateArTranAllocations([arTranAllocation]))),
    takeUntil(this._unsubscribe$),
  );

  constructor(
    public configService: ConfigService,
    protected store: Store,
  ) {
    this.onArTranAllocationUpdate$.subscribe();
  }

  deactivatePayPartial(invoiceTableDataObject: PayableTableObject): void {
    this.amountToPay = this.getMaximumAmount(invoiceTableDataObject);
    this.payPartialActive = false;
    this._arTranAllocationUpdate$.next([invoiceTableDataObject, invoiceTableDataObject.amountRemaining]);
  }

  updateAmountForPartialPayment(invoiceTableDataObject: PayableTableObject): void {
    this.validate(invoiceTableDataObject);
    this._arTranAllocationUpdate$.next([invoiceTableDataObject, +this.amountToPay]);
  }

  getMaximumAmount(invoiceTableDataObject: PayableTableObject) {
    // Rounded so this agrees with PaymentsState.hasInvalidArTranAllocations, which gates the Pay button.
    // An unrounded remainder (e.g. 1234.56 - 234.44 = 1000.1199999999999) renders as $1,000.12, so typing
    // the displayed amount would otherwise read as an over-entry here but not there.
    return roundMonetaryValue(invoiceTableDataObject.invoice.arTran.amount - invoiceTableDataObject.invoice.arTran.allocated);
  }

  getRemainingAfterPayment(invoiceTableDataObject: PayableTableObject): number {
    return roundMonetaryValue(this.getMaximumAmount(invoiceTableDataObject) - +this.amountToPay);
  }

  validate(invoiceTableDataObject: PayableTableObject): void {
    this.valid = this.amountToPay && invoiceTableDataObject && this.amountToPay > 0 && this.amountToPay <= this.getMaximumAmount(invoiceTableDataObject);
  }

  onDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }
}
